import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Tabs — clean underlined tab pattern (Linear / Stripe style)
 *
 * Usage:
 *   <Tabs
 *     items={[
 *       { id: "overview", label: "Overview", count: 3 },
 *       { id: "tailored", label: "Tailored Resume" },
 *     ]}
 *     defaultId="overview"
 *     queryKey="tab"        // optional — sync the URL ?tab=... for deep-linking
 *   >
 *     {(activeId) => (
 *       <>
 *         {activeId === "overview" && <Overview />}
 *         {activeId === "tailored" && <Tailored />}
 *       </>
 *     )}
 *   </Tabs>
 *
 * Props
 *   items     [{ id, label, count?, disabled?, testid? }]
 *   defaultId  initial active tab id
 *   onChange  optional (newId) => void
 *   testid    optional root data-testid
 *   className optional wrapper class
 *   queryKey  optional URL search-param name; when set, the active tab is
 *             read/written to `?{queryKey}=<id>` so the tab can be deep-linked.
 */
export default function Tabs({
  items,
  defaultId,
  onChange,
  testid = "tabs",
  className = "",
  queryKey = null,
  children,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const validIds = items.filter((t) => !t.disabled).map((t) => t.id);
  const queryActive = queryKey ? searchParams.get(queryKey) : null;
  const initial = (queryActive && validIds.includes(queryActive))
    ? queryActive
    : (defaultId || items[0]?.id || null);
  const [active, setActive] = useState(initial);

  // Keep state in sync if the URL changes (browser back/forward, external link).
  useEffect(() => {
    if (!queryKey) return;
    const fromUrl = searchParams.get(queryKey);
    if (fromUrl && fromUrl !== active && validIds.includes(fromUrl)) {
      setActive(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const pick = (id) => {
    if (id === active) return;
    setActive(id);
    if (onChange) onChange(id);
    if (queryKey) {
      // Preserve any other query params; only mutate ours.
      const next = new URLSearchParams(searchParams);
      next.set(queryKey, id);
      setSearchParams(next, { replace: false });
    }
  };

  return (
    <div className={className} data-testid={testid}>
      <div
        role="tablist"
        className="flex items-end gap-1 overflow-x-auto"
        style={{
          borderBottom: "1px solid #E2E8F0",
        }}
        data-testid={`${testid}-list`}
      >
        {items.map((t) => {
          const isActive = t.id === active;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${testid}-panel-${t.id}`}
              type="button"
              onClick={() => !t.disabled && pick(t.id)}
              disabled={t.disabled}
              className="relative px-4 py-2.5 text-[13px] font-semibold transition-colors whitespace-nowrap flex items-center gap-1.5"
              style={{
                color: isActive ? "#B91C1C" : t.disabled ? "#CBD5E1" : "#64748B",
                cursor: t.disabled ? "not-allowed" : "pointer",
                background: "transparent",
                borderBottom: isActive
                  ? "2px solid #B91C1C"
                  : "2px solid transparent",
                marginBottom: -1,
              }}
              onMouseEnter={(e) => {
                if (!isActive && !t.disabled) e.currentTarget.style.color = "#0F1B2D";
              }}
              onMouseLeave={(e) => {
                if (!isActive && !t.disabled) e.currentTarget.style.color = "#64748B";
              }}
              data-testid={t.testid || `${testid}-tab-${t.id}`}
            >
              <span>{t.label}</span>
              {typeof t.count === "number" && t.count > 0 && (
                <span
                  className="inline-flex items-center justify-center text-[10px] font-mono-rm font-bold"
                  style={{
                    minWidth: 18, height: 18, borderRadius: 9,
                    padding: "0 6px",
                    background: isActive ? "#FEF2F2" : "#F1F5F9",
                    color: isActive ? "#B91C1C" : "#475569",
                  }}
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${testid}-panel-${active}`}
        className="pt-4 md:pt-5"
        data-testid={`${testid}-panel`}
      >
        {typeof children === "function" ? children(active) : children}
      </div>
    </div>
  );
}
